import React, { useCallback, useEffect, useRef, useState } from "react";
import { getExportPreview, exportInvoice, downloadCsv, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Download, Loader2, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Figure = ({ label, value, testId, tone }) => (
  <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn("font-mono text-sm font-semibold mt-0.5", tone)} data-testid={testId}>{value}</div>
  </div>
);

export const ExportPreviewDialog = ({ invoiceId, system, currency, onClose, onExported }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!system) return;
    setData(null);
    getExportPreview(invoiceId, system)
      .then(setData)
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not build preview");
        onCloseRef.current();
      });
  }, [invoiceId, system]);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await exportInvoice(invoiceId, system, onExported.user);
      downloadCsv(res.filename, res.content);
      toast.success(`Synced to ${res.system_label} — ${res.check.line_count} row(s), ${money(res.check.exported_total, currency)} reconciled`);
      onExported.done();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const c = data?.check;

  return (
    <Dialog open={!!system} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[calc(100vw-2rem)] overflow-hidden" data-testid="export-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Table2 className="h-4 w-4 text-cyan-400" /> Preview {data?.system_label || "export"}
          </DialogTitle>
          <DialogDescription>
            These are the exact rows that will be written to <span className="font-mono">{data?.filename || "…"}</span>. Nothing is recorded
            until you confirm.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div
              data-testid="reconciliation-panel"
              className={cn(
                "rounded-xl border p-3",
                c.balanced ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"
              )}
            >
              <div className="flex items-center gap-2 text-sm font-medium mb-3">
                {c.balanced ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                <span data-testid="balanced-label">{c.balanced ? "Reconciled — export total equals invoice total" : "Out of balance — review before exporting"}</span>
                {c.adjustment_added && (
                  <span className="ml-auto rounded-md bg-amber-500/15 px-2 py-0.5 text-xs text-amber-500" data-testid="adjustment-flag">
                    Adjustment row added
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <Figure label="Rows" value={c.line_count} testId="check-rows" />
                <Figure label="Lines sum" value={money(c.lines_sum, currency)} testId="check-lines-sum" />
                <Figure label="Subtotal" value={money(c.subtotal, currency)} testId="check-subtotal" />
                <Figure label="Tax" value={money(c.tax, currency)} testId="check-tax" />
                <Figure label="Invoice total" value={money(c.total, currency)} testId="check-total" />
                <Figure label="Export total" value={money(c.exported_total, currency)} testId="check-exported-total" tone={c.balanced ? "text-emerald-500" : "text-red-500"} />
              </div>
            </div>

            <div className="rounded-xl border border-border overflow-hidden min-w-0 w-full">
              <div className="overflow-auto d20-scroll max-h-[320px] w-full">
                <table className="text-xs whitespace-nowrap w-max" data-testid="export-preview-table">
                  <thead className="sticky top-0 bg-card">
                    <tr className="border-b border-border text-left text-muted-foreground">
                      {data.header.map((h) => (
                        <th key={h} className="px-3 py-2 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono">
                    {data.rows.map((r, i) => (
                      <tr key={`${i}-${r[0]}`} data-testid="export-preview-row">
                        {r.map((cell, j) => (
                          <td key={`${j}-${String(cell)}`} className="px-3 py-2 max-w-[260px] truncate" title={String(cell)}>
                            {cell === "" ? <span className="text-muted-foreground/40">—</span> : cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
                {data.header.length} columns · {data.rows.length} row{data.rows.length === 1 ? "" : "s"} · scroll horizontally to see every field
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="confirm-export-btn" onClick={confirm} disabled={busy || !data || !data.exportable}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {data && !data.exportable ? "Approve invoice first" : "Download & record sync"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
